import React from "react";
import { Scale } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

/** Where "Claim Your State" and the waitlist send their enquiry. */
const BANNER_EMAIL = "admin@lawfusion.ai";

/**
 * Exclusive statewide banner advertising.
 *
 * Placement rules — these are product constraints, not styling preferences:
 *   - This must remain the LAST element on the page.
 *   - It must never appear between the question form, the summary, or the
 *     Find an Attorney CTA. Those sections stay ad-free.
 *
 * Inventory rules:
 *   - One advertiser per state, $1,000/month, no rotation.
 *   - Which banner shows is decided by the state the visitor selected.
 *   - No sponsor for that state -> the generic "Claim Your State" fallback.
 *   - Sold out for that state -> offer a waitlist, never a purchase.
 *
 * This is a SEPARATE product from Featured Listings. Do not merge the two.
 *
 * @param {string} usState   - state selected on the question form
 * @param {object|null} sponsor - the state's advertiser, once inventory exists
 * @param {boolean} soldOut  - state is sold for the period; offer a waitlist
 */
export default function StatewideBanner({
  usState = "",
  sponsor = null,
  soldOut = false,
}) {
  const { darkMode } = useTheme();
  const stateLabel = usState || "Your State";

  // Previously "/contact?banner=claim" — a route that does not exist, so the
  // button was a dead link that left the site entirely. The enquiry goes to the
  // same pre-filled email that "Claim Your State" uses on My Directory Listing.
  const enquiryMailto = `mailto:${BANNER_EMAIL}?subject=${encodeURIComponent(
    soldOut
      ? `Statewide Banner waitlist — ${stateLabel}`
      : `Exclusive Statewide Banner — ${stateLabel}`,
  )}&body=${encodeURIComponent(
    soldOut
      ? `Hello LawFusion team,\n\nPlease add me to the waitlist for the exclusive statewide banner in ${stateLabel}.\n\nThank you.`
      : `Hello LawFusion team,\n\nI would like to claim the exclusive statewide banner placement for ${stateLabel}.\n\nPlease send me the details.\n\nThank you.`,
  )}`;

  // A live sponsor takes the slot outright — no house copy alongside it.
  if (sponsor) {
    return (
      <section
        className={`sb-wrap sb-live ${darkMode ? "sb-dark" : ""}`}
        aria-label="Advertisement"
      >
        <span className="sb-tag">Advertisement</span>
        <div className="sb-left">
          {sponsor.logoUrl ? (
            <img className="sb-logo" src={sponsor.logoUrl} alt="" />
          ) : (
            <div className="sb-icon">
              <Scale size={21} />
            </div>
          )}
          <div>
            <h2 className="sb-title">{sponsor.headline}</h2>
            <p className="sb-body">{sponsor.body}</p>
          </div>
        </div>
        <a className="sb-cta" href={sponsor.url} rel="nofollow sponsored noopener noreferrer" target="_blank">
          {sponsor.ctaLabel || "Learn More"}
        </a>
      </section>
    );
  }

  return (
    <section
      className={`sb-wrap ${darkMode ? "sb-dark" : ""}`}
      aria-label="Advertisement"
    >
      <span className="sb-tag">Advertisement</span>
      <div className="sb-left">
        <div className="sb-icon">
          <Scale size={21} />
        </div>
        <div>
          <p className="sb-eyebrow">Exclusive Statewide Banner</p>
          <h2 className="sb-title">
            {soldOut
              ? `${stateLabel} is currently taken`
              : "Be the Exclusive Banner Advertiser in Your State"}
          </h2>
          {/* Who the placement is sold to. Without it the block reads as
              though it were aimed at the consumers browsing this page. */}
          <p className="sb-audience">For Attorneys and Paralegals</p>
          <p className="sb-body">
            {soldOut
              ? `Another firm holds the exclusive banner for ${stateLabel}. Join the waitlist and we'll be in touch when it next becomes available.`
              : `Your banner will be shown on LawFusion's consumer legal-question page to users who select your state. $1,000/month · one advertiser per state · no rotation.`}
          </p>
          <p className="sb-fine">
            Separate advertising placement — not a featured directory listing.
            Subject to availability.
          </p>
        </div>
      </div>
      {/* A sold-out state offers a waitlist, never a purchase. */}
      <a className="sb-cta" href={enquiryMailto}>
        {soldOut ? "Join the Waitlist" : "Claim Your State"}
      </a>
    </section>
  );
}
